/**
 * Trip CO2 Calculator — share URL encoding/decoding + share-text formatting.
 *
 * Phase 8 of the Trip CO2 Calculator (PRD §5.10, §11 Phase 8).
 *
 * Three concerns, kept in one module because they're tightly coupled:
 *
 *   1. `encodeShareParams(trip)` — turns a SharedTrip into a URLSearchParams
 *      object using the readable scheme described below.
 *   2. `decodeShareParams(params)` — parses an incoming URLSearchParams (the
 *      page's query string) back into a SharedTrip, or returns null when
 *      the params are missing/malformed. Silent fall-through is the
 *      contract: a bad link should land the user on an empty page, not a
 *      red error banner.
 *   3. `formatShareText(input)` — dynamic share-payload text. Uses the
 *      comparison ratio (chosen vs train, or train vs plane) rather than
 *      raw kg because the ratio is the more click-worthy hook.
 *
 * URL scheme (readable; SEO-friendly; faithful round-trip):
 *
 *   /travel-impact-calculator
 *     ?fromName=Berlin&fromLat=52.52&fromLng=13.405
 *     &toName=Barcelona&toLat=41.385&toLng=2.173
 *     &mode=airplane
 *     &rt=1
 *     [&carPax=3&carFuel=electric]   // car only, only when non-default
 *     [&vanPax=4]                    // van only, only when non-default
 *     [&moPax=2]                     // motorcycle only, only when non-default
 *
 * Default-config keys are omitted to keep the URL short — a faithful
 * round-trip is still guaranteed because `decodeShareParams` fills in the
 * mode's default for any missing sub-config key.
 *
 * Caveat: if a mode's defaults ever change (e.g. car default fuel goes
 * electric), old shared URLs resolve against the *new* default and
 * silently produce a different result. Acceptable for v1 with no
 * meaningful share history; if/when this becomes a problem, version the
 * URL with `v=1` and migrate.
 */

import type { CityResult } from '../geocoding/api';
import type { ChartRow } from './buildComparison';
import {
  CAR_FUELS,
  DEFAULT_CONFIG,
  MODE_DISPLAY,
  MODE_ORDER,
  OCCUPANCY_RANGES,
  type ConfigByMode,
  type TransportMode,
} from './modes';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface SharedTrip {
  origin: CityResult;
  destination: CityResult;
  mode: TransportMode;
  config: ConfigByMode[TransportMode];
  roundTrip: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Encode
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Lat/lng are clamped to 5 decimals (≈ 1m precision) — plenty for trip-level
 * accuracy, keeps the URL tidy.
 */
const COORD_DECIMALS = 5;

export function encodeShareParams(trip: SharedTrip): URLSearchParams {
  const params = new URLSearchParams();
  params.set('fromName', trip.origin.name);
  params.set('fromLat', trip.origin.lat.toFixed(COORD_DECIMALS));
  params.set('fromLng', trip.origin.lng.toFixed(COORD_DECIMALS));
  params.set('toName', trip.destination.name);
  params.set('toLat', trip.destination.lat.toFixed(COORD_DECIMALS));
  params.set('toLng', trip.destination.lng.toFixed(COORD_DECIMALS));
  params.set('mode', trip.mode);
  params.set('rt', trip.roundTrip ? '1' : '0');

  // Per-vehicle sub-config: only when (a) mode supports it AND
  // (b) the value differs from that mode's locked default.
  if (trip.mode === 'car') {
    const c = trip.config as ConfigByMode['car'];
    const def = DEFAULT_CONFIG.car;
    if (c.occupancy !== def.occupancy) params.set('carPax', String(c.occupancy));
    if (c.fuel !== def.fuel) params.set('carFuel', c.fuel);
  } else if (trip.mode === 'van') {
    const c = trip.config as ConfigByMode['van'];
    const def = DEFAULT_CONFIG.van;
    if (c.occupancy !== def.occupancy) params.set('vanPax', String(c.occupancy));
  } else if (trip.mode === 'motorcycle') {
    const c = trip.config as ConfigByMode['motorcycle'];
    const def = DEFAULT_CONFIG.motorcycle;
    if (c.occupancy !== def.occupancy) params.set('moPax', String(c.occupancy));
  }

  return params;
}

/**
 * Full share URL — `${origin}/travel-impact-calculator?…`. `origin`
 * defaults to `window.location.origin` so callers can omit it in browser
 * contexts; pass an explicit value (e.g. `'https://app.example.com'`) for
 * SSR or tests.
 */
export function buildShareUrl(
  trip: SharedTrip,
  origin: string = typeof window !== 'undefined' ? window.location.origin : ''
): string {
  return `${origin}/travel-impact-calculator?${encodeShareParams(trip).toString()}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Trip equality
// ─────────────────────────────────────────────────────────────────────────────

/**
 * True iff two trips are functionally identical from the calculator's
 * point of view: same origin (lat/lng/name), same destination, same
 * mode + config, same round-trip flag. Used by the page to disable the
 * Calculate button when the user hasn't changed anything since the
 * last submission (or since a URL-deeplinked trip auto-loaded).
 *
 * City `id` is *not* compared — share-decoded cities use a synthetic
 * id (`share:Name:lat,lng`) that wouldn't match a freshly-geocoded
 * city even when the underlying lat/lng/name are identical. Comparing
 * the canonical fields (name + coords) does the right thing in both
 * the manual-typing and deeplinked-trip flows.
 */
export function isSameTrip(
  a: SharedTrip | null | undefined,
  b: SharedTrip | null | undefined
): boolean {
  if (!a || !b) return false;
  if (a.origin.name !== b.origin.name) return false;
  if (a.origin.lat !== b.origin.lat || a.origin.lng !== b.origin.lng) return false;
  if (a.destination.name !== b.destination.name) return false;
  if (a.destination.lat !== b.destination.lat || a.destination.lng !== b.destination.lng) {
    return false;
  }
  if (a.mode !== b.mode) return false;
  if (a.roundTrip !== b.roundTrip) return false;
  return isSameConfig(a.config, b.config);
}

function isSameConfig(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    if (a[k] !== b[k]) return false;
  }
  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// Decode
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the parsed trip, or `null` for any of the following:
 *   • Missing required params (from/to coords + names, mode, rt)
 *   • Mode not in `MODE_ORDER`
 *   • Coords not finite numbers or outside (-90,90) / (-180,180)
 *
 * Conditional sub-config (carPax, carFuel, vanPax, moPax) is best-effort:
 * present-and-valid → use the value, missing/invalid → fall back to the
 * mode's default. That keeps the page robust to old links from before a
 * URL-scheme change.
 */
export function decodeShareParams(params: URLSearchParams): SharedTrip | null {
  const fromName = params.get('fromName');
  const fromLat = params.get('fromLat');
  const fromLng = params.get('fromLng');
  const toName = params.get('toName');
  const toLat = params.get('toLat');
  const toLng = params.get('toLng');
  const modeRaw = params.get('mode');
  const rt = params.get('rt');

  if (!fromName || !toName || !modeRaw) return null;
  if (!fromLat || !fromLng || !toLat || !toLng) return null;
  if (!(MODE_ORDER as readonly string[]).includes(modeRaw)) return null;

  const fromLatN = Number(fromLat);
  const fromLngN = Number(fromLng);
  const toLatN = Number(toLat);
  const toLngN = Number(toLng);
  if (!isValidLat(fromLatN) || !isValidLat(toLatN)) return null;
  if (!isValidLng(fromLngN) || !isValidLng(toLngN)) return null;

  const mode = modeRaw as TransportMode;
  const origin: CityResult = makeCityFromShare(fromName, fromLatN, fromLngN);
  const destination: CityResult = makeCityFromShare(toName, toLatN, toLngN);
  const config = decodeConfig(mode, params);
  const roundTrip = rt === '1';

  return { origin, destination, mode, config, roundTrip };
}

function makeCityFromShare(name: string, lat: number, lng: number): CityResult {
  return {
    // Synthetic id — distinguishes share-decoded cities from geocoder
    // results without colliding (CityResult.id is a free-form string).
    id: `share:${name}:${lat.toFixed(COORD_DECIMALS)},${lng.toFixed(COORD_DECIMALS)}`,
    name,
    displayName: name,
    country: null,
    region: null,
    lat,
    lng,
  };
}

function decodeConfig<M extends TransportMode>(
  mode: M,
  params: URLSearchParams
): ConfigByMode[M] {
  if (mode === 'car') {
    const def = DEFAULT_CONFIG.car;
    const occupancy = parseIntInRange(
      params.get('carPax'),
      OCCUPANCY_RANGES.car.min,
      OCCUPANCY_RANGES.car.max
    );
    const fuelRaw = params.get('carFuel');
    const fuel = fuelRaw && (CAR_FUELS as readonly string[]).includes(fuelRaw)
      ? (fuelRaw as ConfigByMode['car']['fuel'])
      : def.fuel;
    return { fuel, occupancy: occupancy ?? def.occupancy } as ConfigByMode[M];
  }
  if (mode === 'van') {
    const def = DEFAULT_CONFIG.van;
    const occupancy = parseIntInRange(
      params.get('vanPax'),
      OCCUPANCY_RANGES.van.min,
      OCCUPANCY_RANGES.van.max
    );
    return { fuel: def.fuel, occupancy: occupancy ?? def.occupancy } as ConfigByMode[M];
  }
  if (mode === 'motorcycle') {
    const def = DEFAULT_CONFIG.motorcycle;
    const occupancy = parseIntInRange(
      params.get('moPax'),
      OCCUPANCY_RANGES.motorcycle.min,
      OCCUPANCY_RANGES.motorcycle.max
    );
    return { fuel: def.fuel, occupancy: occupancy ?? def.occupancy } as ConfigByMode[M];
  }
  // train / bus / airplane / ferry have no editable sub-config.
  return { ...DEFAULT_CONFIG[mode] } as ConfigByMode[M];
}

function parseIntInRange(value: string | null, min: number, max: number): number | null {
  if (value === null) return null;
  const n = Number.parseInt(value, 10);
  if (!Number.isFinite(n) || n < min || n > max) return null;
  return n;
}

function isValidLat(n: number): boolean {
  return Number.isFinite(n) && n >= -90 && n <= 90;
}
function isValidLng(n: number): boolean {
  return Number.isFinite(n) && n >= -180 && n <= 180;
}

// ─────────────────────────────────────────────────────────────────────────────
// Share text formatter
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Dynamic share-payload copy. Two-paragraph format:
 *
 *   "Did you know that a {origin} → {destination} trip by {mode}
 *    emits {N× | N%} more CO₂e than the same trip by train?
 *
 *    See the full impact report at Jody's Travel Impact Calculator:"
 *
 * The URL is appended on a third line by the consumer (the share
 * dialog joins `shareText + '\n' + shareUrl`). Having a "naked" URL
 * on its own line lets iMessage / WhatsApp / Slack / etc. generate
 * richer link-preview cards.
 *
 * The "did you know" question framing creates an open loop in the
 * recipient's mind that the comparison ratio resolves — more
 * click-worthy than an absolute kg figure (which most people can't
 * intuit without a reference).
 *
 * Three branches:
 *   • Non-train mode with usable delta → "by plane emits 13× more
 *     CO₂e than the same trip by train".
 *   • Train mode → "by train emits 92% less CO₂e than the same trip
 *     by plane" (positive framing for users who already picked train).
 *   • No usable delta (chosen ≈ train, missing comparator, etc.) →
 *     drops to a safe generic "how … emits CO₂e by different
 *     transport modes" form.
 */
export function formatShareText(input: {
  rows: ChartRow[];
  userMode: TransportMode;
  originName: string;
  destinationName: string;
}): string {
  const { rows, userMode, originName, destinationName } = input;
  const route = `${originName} → ${destinationName}`;
  const signOff = `See the full impact report at Jody's Travel Impact Calculator:`;
  const generic = `Did you know how a ${route} trip emits CO₂e by different transport modes?`;
  const modeLabel = MODE_DISPLAY[userMode].label.toLowerCase();

  const question = (() => {
    const chosen = rows.find((r) => r.isChosen);
    if (!chosen || chosen.kg <= 0) return generic;

    const isTrain = userMode === 'train';
    const comparator = rows.find((r) => r.id === (isTrain ? 'airplane' : 'train'));
    if (!comparator || comparator.kg <= 0) {
      // Defensive absolute fallback — both endpoint rows missing.
      return `Did you know that a ${route} trip by ${modeLabel} emits ~${Math.round(
        chosen.kg
      ).toLocaleString()} kg CO₂e per passenger?`;
    }

    if (isTrain) {
      const percent = Math.round((1 - chosen.kg / comparator.kg) * 100);
      if (percent <= 0) return generic;
      return `Did you know that a ${route} trip by train emits ${percent}% less CO₂e than the same trip by plane?`;
    }

    // Non-train mode: chosen-vs-train delta.
    const ratio = chosen.kg / comparator.kg;
    if (ratio <= 1.05) return generic;

    let comparison: string;
    if (ratio >= 2) {
      // Round "2.0×" → "2×" but keep "2.4×" with the decimal. Above 5×
      // the decimal is visually noisy → round to integer.
      const nx =
        ratio < 5 ? ratio.toFixed(1).replace(/\.0$/, '') : Math.round(ratio).toString();
      comparison = `${nx}× more CO₂e than the same trip by train`;
    } else {
      const percent = Math.round((ratio - 1) * 100);
      comparison = `${percent}% more CO₂e than the same trip by train`;
    }
    return `Did you know that a ${route} trip by ${modeLabel} emits ${comparison}?`;
  })();

  // Blank line between the question and the call-to-action so the
  // sign-off reads as a separate paragraph; the URL gets its own
  // line when the dialog joins shareText + shareUrl with '\n'.
  return `${question}\n\n${signOff}`;
}
